import React, { useMemo, useState } from 'react';

import { Button } from 'components/ui/button';
import { Dialog } from '@buildoutinc/blueprint-react/ui/Dialog';
import { showSnackbar } from 'components/ui/snackbar/helper';

import { put } from 'helpers/api';

import OupUserSelector from 'bundles/company/permissions/oup_user_selector';
import PermissionCheckboxCard from './permission_checkbox_card';
import OfficeAdminCard from './office_admin_card';
import { applyDependencies, dependencyDisabled } from './permission_dependencies';

import type { OupMode } from 'bundles/company/permissions/oup_user_selector';
import type { PermissionCard, PermissionDescriptor, PermissionSection, UserProfile } from 'components/users/types';

type Props = Pick<UserProfile, 'permissions' | 'paths'>;

function isDescriptor(card: PermissionCard): card is PermissionDescriptor {
  return 'attr' in card;
}

function initialCheckboxes(sections: PermissionSection[]): Record<string, boolean> {
  const values: Record<string, boolean> = {};

  sections.forEach((section) => {
    section.cards.forEach((card) => {
      if (isDescriptor(card)) values[card.attr] = card.value;
    });
  });

  return values;
}

function initialOupMode(selectedUserIds: number[], selectedOfficeIds: number[]): OupMode {
  if (selectedOfficeIds.length > 0) return 'offices';
  if (selectedUserIds.length > 0) return 'individual';
  return 'all';
}

export default function PermissionsTab({ permissions, paths }: Props) {
  const { canManage, sections, officeAdmin, oup, dependencyRules } = permissions;

  const [checkboxes, setCheckboxes] = useState<Record<string, boolean>>(
    () => applyDependencies(dependencyRules, initialCheckboxes(sections), officeAdmin?.value ?? false),
  );
  const [officeAdminChecked, setOfficeAdminChecked] = useState(officeAdmin?.value ?? false);
  const [adminedOfficeIds, setAdminedOfficeIds] = useState<number[]>(officeAdmin?.selectedOfficeIds ?? []);

  // Drop any stored OUP user IDs that aren't in the available users list (e.g. a
  // deactivated user) so they don't linger in state and get re-saved.
  const [oupUserIds, setOupUserIds] = useState<number[]>(() => {
    const availableUserIds = new Set(oup.users.map(user => user.id));
    return oup.selectedUserIds.filter(id => availableUserIds.has(id));
  });
  const [oupOfficeIds, setOupOfficeIds] = useState<number[]>(oup.selectedOfficeIds);
  const [oupMode, setOupMode] = useState<OupMode>(initialOupMode(oup.selectedUserIds, oup.selectedOfficeIds));

  const [saving, setSaving] = useState(false);

  // Some permissions require explicit confirmation before they can be enabled.
  // While a confirm is pending we hold the attr whose message to show.
  const [pendingConfirmAttr, setPendingConfirmAttr] = useState<string | null>(null);

  const confirmMessages = useMemo(() => {
    const messages: Record<string, string> = {};

    sections.forEach((section) => {
      section.cards.forEach((card) => {
        if (isDescriptor(card) && card.confirmMessage) messages[card.attr] = card.confirmMessage;
      });
    });

    return messages;
  }, [sections]);

  function applyCheckboxChange(attr: string, value: boolean) {
    // Re-normalize so dependent permissions stay consistent (e.g. clearing a
    // prerequisite clears the permissions that require it). Pass the prior state
    // so revoking voucher access also releases the deal access it had forced on.
    setCheckboxes(prev => applyDependencies(dependencyRules, { ...prev, [attr]: value }, officeAdminChecked, prev));
  }

  function handleCheckboxChange(attr: string, value: boolean) {
    if (value && confirmMessages[attr]) {
      setPendingConfirmAttr(attr);
      return;
    }

    applyCheckboxChange(attr, value);
  }

  function handleConfirm() {
    if (pendingConfirmAttr) applyCheckboxChange(pendingConfirmAttr, true);
    setPendingConfirmAttr(null);
  }

  function handleOfficeAdminToggle(value: boolean) {
    setOfficeAdminChecked(value);
    // Office admin grants voucher access, so toggling it re-locks that permission
    // (and releases the deal access that voucher access had forced on).
    setCheckboxes(prev => applyDependencies(dependencyRules, prev, value, prev));
  }

  function buildPayload() {
    const payload: Record<string, unknown> = { ...checkboxes };

    if (officeAdmin) {
      payload.officeAdmin = officeAdminChecked;
      payload.adminedOfficeIds = officeAdminChecked ? adminedOfficeIds : [];
    }

    if (oupMode === 'offices') {
      payload.permissionsOtherOfficeIds = oupOfficeIds;
    } else {
      payload.permissionsOtherUserIds = oupMode === 'individual' ? oupUserIds : [];
      payload.clearPermissionsOtherOfficeIds = true;
    }

    return payload;
  }

  async function handleSave() {
    setSaving(true);

    try {
      await put(paths.update, { user: buildPayload() });
      showSnackbar({ text: 'Your permissions were saved.', variant: 'success' });
    } catch (error) {
      const messages = Array.isArray(error) ? error : [];
      const text = messages.length > 0
        ? messages.join(' ')
        : 'Something went wrong saving your permissions.';
      showSnackbar({ text, variant: 'danger' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {sections.filter(section => section.cards.length > 0).map(section => (
        <div className="mb-2" key={section.title ?? 'main'}>
          {section.title && <h5 className="mt-8 mb-3">{section.title}</h5>}
          <div className="row gy-4">
            {section.cards.map((card) => {
              if (isDescriptor(card)) {
                const lockedByDependency = dependencyDisabled(
                  dependencyRules, card.attr, checkboxes, officeAdminChecked,
                );

                return (
                  <PermissionCheckboxCard
                    checked={checkboxes[card.attr]}
                    descriptor={card}
                    disabled={card.disabled || lockedByDependency}
                    key={card.attr}
                    onChange={handleCheckboxChange}
                  />
                );
              }

              return officeAdmin ? (
                <OfficeAdminCard
                  checked={officeAdminChecked}
                  data={officeAdmin}
                  key="office_admin"
                  selectedOfficeIds={adminedOfficeIds}
                  onChangeOffices={setAdminedOfficeIds}
                  onToggle={handleOfficeAdminToggle}
                />
              ) : null;
            })}
          </div>
        </div>
      ))}

      <hr className="mt-6" />

      <OupUserSelector
        mode={oupMode}
        officesById={oup.officesById}
        selectedOfficeIds={oupOfficeIds}
        selectedUserIds={oupUserIds}
        users={oup.users}
        onChangeMode={setOupMode}
        onChangeOfficeIds={setOupOfficeIds}
        onChangeUserIds={setOupUserIds}
      />

      {canManage && (
        <Button className="mt-6" disabled={saving} onClick={handleSave}>
          Save Permissions
        </Button>
      )}

      <Dialog open={pendingConfirmAttr !== null} onClose={() => setPendingConfirmAttr(null)}>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Confirm Permission Change</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            <p>{pendingConfirmAttr ? confirmMessages[pendingConfirmAttr] : ''}</p>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Cancel>Cancel</Dialog.Cancel>
            <Button onClick={handleConfirm}>Confirm</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </div>
  );
}
